import React, { useRef, useState } from "react";
import { Camera, Plus, Trash2 } from "lucide-react";
import {
    createChecklistItem,
    parseChecklist,
    taskFileUrl,
} from "../utils/taskChecklist";

const TaskChecklist = ({
    items = [],
    onChange,
    onUploadPhoto,
    apiUrl = "",
    canEdit = false,
    canToggle = true,
    disabled = false,
    actor = null,
}) => {
    const list = parseChecklist(items);
    const [draft, setDraft] = useState("");
    const [required, setRequired] = useState(false);
    const [photoRequired, setPhotoRequired] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);
    const fileRef = useRef(null);
    const pendingItemId = useRef(null);

    const addItem = () => {
        const text = draft.trim();
        if (!text) return;
        onChange?.([...list, createChecklistItem(text, required, photoRequired)]);
        setDraft("");
        setRequired(false);
        setPhotoRequired(false);
    };

    const toggleItem = (item) => {
        if (disabled || !canToggle) return;
        if (item.photoRequired && !item.photo && !item.done) {
            pendingItemId.current = item.id;
            fileRef.current?.click();
            return;
        }
        onChange?.(
            list.map((row) => {
                if (row.id !== item.id) return row;
                const done = !row.done;
                return {
                    ...row,
                    done,
                    doneAt: done ? new Date().toISOString() : null,
                    doneBy: done ? actor : null,
                };
            })
        );
    };

    const pickPhoto = (itemId) => {
        pendingItemId.current = itemId;
        fileRef.current?.click();
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        const itemId = pendingItemId.current;
        e.target.value = "";
        if (!file || !itemId || !onUploadPhoto) return;
        setUploadingId(itemId);
        try {
            await onUploadPhoto(itemId, file);
        } finally {
            setUploadingId(null);
            pendingItemId.current = null;
        }
    };

    return (
        <div className="task-checklist">
            <div className="task-checklist__head">
                <h3 className="task-checklist__title">Checklist</h3>
                {list.length > 0 && (
                    <span className="task-checklist__count">
                        {list.filter((i) => i.done).length}/{list.length}
                    </span>
                )}
            </div>

            {list.length === 0 && (
                <p className="task-checklist__empty">No checklist items yet.</p>
            )}

            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="task-checklist__file"
                onChange={onFile}
            />

            <ul className="task-checklist__list">
                {list.map((item) => {
                    const photoSrc = taskFileUrl(apiUrl, item.photo);
                    return (
                        <li key={item.id} className="task-checklist__item">
                            <label className="task-checklist__row">
                                <input
                                    type="checkbox"
                                    checked={item.done}
                                    disabled={disabled || !canToggle}
                                    onChange={() => toggleItem(item)}
                                />
                                <span className={item.done ? "is-done" : ""}>
                                    {item.text}
                                    {item.required && (
                                        <span className="task-checklist__req">Required</span>
                                    )}
                                    {item.photoRequired && (
                                        <span className="task-checklist__req">Photo</span>
                                    )}
                                </span>
                            </label>
                            {photoSrc && (
                                <a
                                    href={photoSrc}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="task-checklist__thumb"
                                >
                                    <img src={photoSrc} alt="" />
                                </a>
                            )}
                            {canToggle && !disabled && onUploadPhoto && (
                                <button
                                    type="button"
                                    className="task-checklist__photo"
                                    onClick={() => pickPhoto(item.id)}
                                    disabled={uploadingId === item.id}
                                    aria-label="Add step photo"
                                >
                                    <Camera size={14} />
                                </button>
                            )}
                            {canEdit && (
                                <button
                                    type="button"
                                    className="task-checklist__delete"
                                    onClick={() =>
                                        onChange?.(list.filter((row) => row.id !== item.id))
                                    }
                                    aria-label="Remove checklist item"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </li>
                    );
                })}
            </ul>

            {canEdit && (
                <div className="task-checklist__add">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addItem();
                            }
                        }}
                        placeholder="Add a step"
                        disabled={disabled}
                    />
                    <label className="task-checklist__flag">
                        <input
                            type="checkbox"
                            checked={required}
                            onChange={(e) => setRequired(e.target.checked)}
                        />
                        Required
                    </label>
                    <label className="task-checklist__flag">
                        <input
                            type="checkbox"
                            checked={photoRequired}
                            onChange={(e) => setPhotoRequired(e.target.checked)}
                        />
                        Photo
                    </label>
                    <button type="button" onClick={addItem} disabled={!draft.trim()}>
                        <Plus size={14} />
                        Add
                    </button>
                </div>
            )}
        </div>
    );
};

export default TaskChecklist;
